import { gql, useQuery } from '@apollo/client';
import IconCloud from '@apollo/icons/default/IconCloud.svg';
import {
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
// TODO: Remove use of `ModalCloseButton`
// eslint-disable-next-line no-restricted-imports
import { ModalCloseButton } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import {
  explorerRouteConfig,
  subgraphsRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import { CompositionErrorsBanner } from 'src/app/graph/subgraphsLayout/subgraphsPage/addSubgraphModal/CompositionErrorsBanner';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { ContactSupportButton } from 'src/components/ContactSupportCTAs';
import {
  IsCloudEndpointInitiatingContextProvider,
  useIsCloudEndpointInitiatingContext,
} from 'src/hooks/useIsCloudEndpointInitiating';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { triggerFullPageConfetti } from 'src/lib/confetti';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const InitiatingNewSupergraphEndpointModal = ({
  graphRef,
  onClose,
}: {
  graphRef: GraphRef;
  onClose: () => void;
}) => {
  const { data, loading } = useQuery<
    GraphQLTypes.InitiatingNewSupergraphEndpointModal_RouterEndpointQuery,
    GraphQLTypes.InitiatingNewSupergraphEndpointModal_RouterEndpointQueryVariables
  >(
    gql`
      query InitiatingNewSupergraphEndpointModal_RouterEndpointQuery(
        $graphRef: ID!
      ) {
        variant(ref: $graphRef) {
          ... on GraphVariant {
            id
            subgraphs {
              name
            }
            router {
              id
              routerUrl
            }
          }
        }
      }
    `,
    {
      variables: {
        graphRef: graphRefToString(graphRef),
      },
    },
  );

  const variant =
    data?.variant?.__typename === 'GraphVariant' ? data.variant : undefined;

  const routerUrl = variant?.router?.routerUrl;

  return (
    <IsCloudEndpointInitiatingContextProvider graphRef={graphRef}>
      <InitiatingNewSupergraphEndpointModalContent
        graphRef={graphRef}
        onClose={onClose}
        routerUrl={routerUrl}
        variant={variant}
        loadingData={loading}
      />
    </IsCloudEndpointInitiatingContextProvider>
  );
};

const InitiatingNewSupergraphEndpointModalContent = ({
  graphRef,
  onClose: onCloseModal,
  routerUrl,
  variant,
  loadingData,
}: {
  graphRef: GraphRef;
  onClose: () => void;
  routerUrl: string | undefined | null;
  variant:
    | GraphQLTypes.InitiatingNewSupergraphEndpointModal_RouterEndpointQuery_variant_GraphVariant
    | undefined;
  loadingData: boolean;
}) => {
  const [isInitiating, setIsInitiating] = useState(true);
  const { routerIsInitiating, routerStatus, buildErrors, loading } =
    useIsCloudEndpointInitiatingContext();

  useEffect(() => {
    if (
      !loading &&
      !routerIsInitiating &&
      routerStatus === GraphQLTypes.RouterStatus.RUNNING
    ) {
      triggerFullPageConfetti();
    }
  }, [loading, routerIsInitiating, routerStatus]);

  useEffect(() => {
    if (
      (isInitiating && routerStatus === GraphQLTypes.RouterStatus.CREATING) ||
      routerStatus === GraphQLTypes.RouterStatus.RUNNING ||
      buildErrors?.length
    ) {
      setIsInitiating(false);
    }
  }, [buildErrors?.length, isInitiating, routerStatus]);

  const trackCustomEvent = useTrackCustomEvent();
  const onClose = useCallback(() => {
    trackCustomEvent({
      action: 'Click',
      category: '6M2S: Onboarding',
      label: 'button:Close Initiating Modal',
    });
    onCloseModal();
  }, [onCloseModal, trackCustomEvent]);

  const showLoading =
    isInitiating || loadingData || loading || routerIsInitiating;

  return (
    // you can only close this modal by the x button, not clicking outside
    <Modal
      onClose={() => null}
      size={
        !showLoading && routerStatus === GraphQLTypes.RouterStatus.RUNNING
          ? 'xl'
          : 'lg'
      }
      isOpen={true}
    >
      <ModalOverlay />
      <ModalContent data-testid="initiating-new-supergraph-endpoint-modal">
        <ModalCloseButton zIndex={2} onClick={onClose} />
        <ModalHeader>
          <ModalTitle>
            {showLoading
              ? 'Almost done!  ️🔮 '
              : routerStatus === GraphQLTypes.RouterStatus.RUNNING
                ? `${graphRefToString(graphRef)} is ready to query 🎉`
                : isMinLengthArray(1, buildErrors)
                  ? `Oh no! The build for ${graphRefToString(graphRef)} failed 😟`
                  : `Oh no! Looks like something went wrong with ${graphRefToString(
                      graphRef,
                    )} 😟`}
          </ModalTitle>
        </ModalHeader>
        {showLoading ? (
          <ModalBody>
            Your new supergraph endpoint is being set up, which can take a few
            seconds. You’ll be notified as soon as it’s ready!
            <div className="flex items-center italic">
              <Spinner size="xs" className="mr-2" />
              Provisioning endpoint for {graphRefToString(graphRef)}{' '}
            </div>
          </ModalBody>
        ) : routerStatus === GraphQLTypes.RouterStatus.RUNNING ? (
          <>
            <ModalBody>
              Your supergraph is now running at
              <div className="flex items-center">
                <IconCloud className="mr-2 mt-1 size-4 self-start text-secondary" />
                {routerUrl ? (
                  <ClickableText
                    as="a"
                    href={routerUrl}
                    className="flex break-all text-base text-link"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {routerUrl}
                  </ClickableText>
                ) : (
                  <>...</>
                )}
              </div>
              You’ll automatically get schema usage metrics, @defer support in
              your queries, and access to all other features of GraphOS. All you
              have to do is start querying!
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  as={ReactRouterLink}
                  to={variantHomepageRouteConfig.location({
                    ...graphRef,
                  })}
                >
                  Check out the README
                </Button>
                <Button
                  variant="primary"
                  as={ReactRouterLink}
                  to={explorerRouteConfig.location({ ...graphRef })}
                >
                  Query your supergraph
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </>
        ) : isMinLengthArray(1, buildErrors) ? (
          <ModalBody>
            <div>
              Fix the build errors and
              <Link
                to={
                  variant?.subgraphs?.[0]?.name
                    ? setOverlayVisibleLocation({
                        location: subgraphsRouteConfig.location({
                          ...graphRef,
                          reuploadSubgraphModalState: {
                            subgraphName: variant.subgraphs[0].name,
                          },
                        }),
                        overlayName: Config.modals.reuploadSubgraph,
                      })
                    : subgraphsRouteConfig.location({ ...graphRef })
                }
              >
                {' '}
                reupload this subgraph
              </Link>
              , or{' '}
              <Link
                to={setOverlayVisibleLocation({
                  location: subgraphsRouteConfig.location({
                    ...graphRef,
                  }),
                  overlayName: Config.modals.addSubgraph,
                })}
              >
                upload a second subgraph
              </Link>{' '}
              to trigger composition.
            </div>
            <CompositionErrorsBanner
              errors={buildErrors.map((error) => error.message)}
              graphRef={graphRef}
            />
          </ModalBody>
        ) : (
          <>
            <ModalBody>
              Try recreating your supergraph, or submit a support ticket. Our
              team will take a look at this for you as soon as possible.
            </ModalBody>

            <ModalFooter>
              <ContactSupportButton variant="secondary">
                Contact support
              </ContactSupportButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
